import { emails, emailIngestion, properties as propertiesApi, documents } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { showModal, confirmModal } from '../components/modal';
import { showToast } from '../components/toast';
import type { InboundEmail, Property } from '../types';

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export async function inboundEmailsPage(): Promise<HTMLElement> {
  const page = document.createElement('div');

  // Header with action buttons
  const headerActions = document.createElement('div');
  headerActions.className = 'flex gap-3';

  const pollBtn = document.createElement('button');
  pollBtn.className = 'btn-primary';
  pollBtn.textContent = 'Poll Emails Now';
  headerActions.appendChild(pollBtn);

  const rematchBtn = document.createElement('button');
  rematchBtn.className = 'btn-outline';
  rematchBtn.textContent = 'Re-match Unmatched';
  headerActions.appendChild(rematchBtn);

  const reclassifyBtn = document.createElement('button');
  reclassifyBtn.className = 'btn-outline';
  reclassifyBtn.textContent = 'Reclassify Emails';
  headerActions.appendChild(reclassifyBtn);

  page.appendChild(createPageHeader('Inbound Emails', headerActions));

  const statusBar = document.createElement('div');
  statusBar.className = 'mb-4 hidden';
  statusBar.innerHTML = '<div class="bg-gray-50 rounded-lg p-3 text-sm"></div>';
  page.appendChild(statusBar);

  // Filter bar
  const filters = document.createElement('div');
  filters.className = 'flex gap-4 mb-6 flex-wrap';
  filters.innerHTML = `
    <select id="email-cat-filter" class="input-field w-auto">
      <option value="">All Categories</option>
      <option value="payment">Payment</option>
      <option value="maintenance">Maintenance</option>
      <option value="lease">Lease</option>
      <option value="general">General</option>
    </select>
    <select id="email-match-filter" class="input-field w-auto">
      <option value="">All Match Status</option>
      <option value="sender_email">Matched by Sender</option>
      <option value="property_address">Matched by Address</option>
      <option value="manual">Matched Manually</option>
      <option value="unmatched">Unmatched</option>
    </select>
  `;
  page.appendChild(filters);

  const tableContainer = document.createElement('div');
  page.appendChild(tableContainer);

  let allItems: InboundEmail[] = [];
  let propertyList: Property[] = [];

  const load = async () => {
    try {
      [allItems, propertyList] = await Promise.all([
        emails.list(),
        propertiesApi.list(),
      ]);
    } catch {
      tableContainer.innerHTML = '<div class="text-red-500 p-4">Failed to load emails.</div>';
      return;
    }
    renderFiltered();
  };

  const renderFiltered = () => {
    const cat = (page.querySelector('#email-cat-filter') as HTMLSelectElement).value;
    const match = (page.querySelector('#email-match-filter') as HTMLSelectElement).value;
    let filtered = allItems;
    if (cat) filtered = filtered.filter(e => e.category === cat);
    if (match) filtered = filtered.filter(e => e.match_method === match);
    renderTable(tableContainer, filtered, propertyList, load);
  };

  filters.querySelectorAll('select').forEach(s => s.addEventListener('change', renderFiltered));

  // Poll button handler
  pollBtn.addEventListener('click', async () => {
    pollBtn.disabled = true;
    pollBtn.textContent = 'Polling...';
    try {
      const res = await emailIngestion.poll();
      statusBar.classList.remove('hidden');
      statusBar.querySelector('div')!.innerHTML = `
        <span class="text-green-600 font-medium">Poll complete.</span>
        Processed: ${res.processed}, Errors: ${res.errors}
      `;
      showToast(`Processed ${res.processed} emails`, 'success');
      await load();
    } catch {
      statusBar.classList.remove('hidden');
      statusBar.querySelector('div')!.innerHTML = '<span class="text-red-500">Poll failed. Check IMAP settings.</span>';
      showToast('Poll failed', 'error');
    }
    pollBtn.disabled = false;
    pollBtn.textContent = 'Poll Emails Now';
  });

  // Re-match button handler
  rematchBtn.addEventListener('click', async () => {
    rematchBtn.disabled = true;
    rematchBtn.textContent = 'Re-matching...';
    try {
      const res = await emails.rematch();
      statusBar.classList.remove('hidden');
      statusBar.querySelector('div')!.innerHTML = `
        <span class="text-green-600 font-medium">Re-match complete.</span>
        Matched: ${res.rematched} of ${res.total_unmatched} unmatched emails.
      `;
      showToast(`Re-matched ${res.rematched} emails`, 'success');
      await load();
    } catch {
      showToast('Re-match failed', 'error');
    }
    rematchBtn.disabled = false;
    rematchBtn.textContent = 'Re-match Unmatched';
  });

  // Reclassify button handler
  reclassifyBtn.addEventListener('click', async () => {
    reclassifyBtn.disabled = true;
    reclassifyBtn.textContent = 'Reclassifying...';
    try {
      const res = await emails.reclassify();
      statusBar.classList.remove('hidden');
      statusBar.querySelector('div')!.innerHTML = `
        <span class="text-green-600 font-medium">Reclassify complete.</span>
        Processed: ${res.reclassified} emails. Payments created: ${res.payments_created}. Maintenance created: ${res.maintenance_created}.
      `;
      showToast(`Reclassified ${res.reclassified} emails — ${res.payments_created} payments, ${res.maintenance_created} maintenance`, 'success');
      await load();
    } catch {
      showToast('Reclassify failed', 'error');
    }
    reclassifyBtn.disabled = false;
    reclassifyBtn.textContent = 'Reclassify Emails';
  });

  await load();
  return page;
}

function renderTable(
  container: HTMLElement,
  items: InboundEmail[],
  propertyList: Property[],
  reload: () => Promise<void>,
): void {
  container.innerHTML = '';

  // Build property lookup
  const propMap = new Map<string, Property>();
  for (const p of propertyList) propMap.set(p.id, p);

  // Helper to get a useful title from parsed_data
  const getTitle = (e: InboundEmail): string => {
    try {
      if (e.parsed_data) {
        const pd = typeof e.parsed_data === 'string' ? JSON.parse(e.parsed_data) : e.parsed_data;
        if (pd.description) return pd.description;
        if (pd.amount && pd.category === 'payment') return `Payment: $${pd.amount}`;
        if (pd.category) return pd.category.charAt(0).toUpperCase() + pd.category.slice(1) + ' notification';
      }
    } catch { /* ignore */ }
    // Clean up subject: remove RE: FW: prefixes and truncate
    return (e.subject || '(no subject)').replace(/^(re:|fw:|fwd:)\s*/gi, '').trim() || '(no subject)';
  };

  const cols: Column<InboundEmail>[] = [
    { label: 'From', render: r => `<span title="${r.from_address}">${r.from_address.split('@')[0]}</span>` },
    { label: 'Title', render: r => `<span class="cursor-pointer hover:text-primary-600" title="${r.subject}">${getTitle(r)}</span>`, className: 'max-w-xs truncate' },
    { label: 'Received', render: r => new Date(r.received_at).toLocaleString() },
    { label: 'Category', render: r => `<span class="badge badge-blue">${r.category}</span>` },
    {
      label: 'Property', render: r => {
        if (r.property_id) {
          const prop = propMap.get(r.property_id);
          if (prop) return `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${prop.address}</a>`;
          return `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_id.substring(0, 8)}...</a>`;
        }
        return '<span class="text-gray-400">Unmatched</span>';
      }
    },
    {
      label: 'Match', render: r => {
        if (!r.match_method || r.match_method === 'unmatched') return '<span class="badge badge-yellow">Unmatched</span>';
        const labels: Record<string, string> = { sender_email: 'Sender', property_address: 'Address', manual: 'Manual' };
        return `<span class="badge badge-green">${labels[r.match_method] || r.match_method}</span>`;
      }
    },
    {
      label: 'Attachments', render: r => {
        if (!r.attachment_paths?.length) return '—';
        const icons = r.attachment_paths.map(p => {
          const name = (p.split('/').pop() || '').toLowerCase();
          if (name.endsWith('.pdf')) return '📄';
          if (name.match(/\.(jpe?g|png|gif|webp|heic)$/)) return '🖼️';
          return '📎';
        });
        const unique = [...new Set(icons)].join(' ');
        return `<span class="text-sm">${unique} ${r.attachment_paths.length} file${r.attachment_paths.length > 1 ? 's' : ''}</span>`;
      }
    },
    {
      label: '', render: r => {
        let btns = '';
        if (!r.match_method || r.match_method === 'unmatched') {
          btns += `<button class="btn-sm btn-outline match-btn text-xs px-2 py-1" data-email-id="${r.id}">Match</button> `;
        }
        btns += `<button class="btn-sm btn-ghost delete-email-btn text-xs px-2 py-1 text-red-400 hover:text-red-600" data-email-id="${r.id}" title="Delete">✕</button>`;
        return btns;
      }
    },
  ];

  const table = createDataTable(cols, items, {
    emptyText: 'No inbound emails received yet.',
    onRowClick: (row) => showEmailDetailModal(row, propMap.get(row.property_id), propertyList, reload),
  });
  container.appendChild(table);

  // Wire match buttons using data-email-id
  table.querySelectorAll('.match-btn').forEach(btn => {
    const emailId = (btn as HTMLElement).dataset.emailId;
    const emailItem = items.find(i => i.id === emailId);
    if (emailItem) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        showMatchModal(emailItem, propertyList, reload);
      });
    }
  });

  // Wire delete buttons
  table.querySelectorAll('.delete-email-btn').forEach(btn => {
    const emailId = (btn as HTMLElement).dataset.emailId;
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const confirmed = await confirmModal('Delete this email? This cannot be undone.');
      if (!confirmed) return;
      try {
        await emails.delete(emailId!);
        showToast('Email deleted', 'success');
        await reload();
      } catch {
        showToast('Failed to delete email', 'error');
      }
    });
  });
}

function showEmailDetailModal(email: InboundEmail, property: Property | undefined, propertyList: Property[], reload: () => Promise<void>): void {
  let parsedInfo = '';
  try {
    if (email.parsed_data) {
      const pd = typeof email.parsed_data === 'string' ? JSON.parse(email.parsed_data) : email.parsed_data;
      const entries = Object.entries(pd).filter(([, v]) => v && v !== '');
      if (entries.length > 0) {
        parsedInfo = `<div class="mt-4"><h4 class="text-sm font-semibold text-gray-700 mb-2">Parsed Data</h4><dl class="space-y-1 text-sm">${
          entries.map(([k, v]) => `<div class="flex justify-between"><dt class="text-gray-500">${k}</dt><dd class="font-medium">${v}</dd></div>`).join('')
        }</dl></div>`;
      }
    }
  } catch { /* ignore */ }

  const attachmentsList = email.attachment_paths?.length
    ? `<div class="mt-4"><h4 class="text-sm font-semibold text-gray-700 mb-2">Attachments (${email.attachment_paths.length})</h4><ul class="text-sm space-y-1">${
        email.attachment_paths.map(p => {
          const name = p.split('/').pop() || p;
          const icon = name.match(/\.pdf$/i) ? '📄' : name.match(/\.(jpe?g|png|gif|webp|heic)$/i) ? '🖼️' : '📎';
          return `<li><button class="email-attachment-link flex items-center gap-2 text-primary-600 hover:underline text-left" data-blob-path="${p}" data-prop-id="${email.property_id || 'unmatched'}">${icon} ${name}</button></li>`;
        }).join('')
      }</ul></div>`
    : '';

  const isMatched = email.property_id && email.property_id !== 'UNMATCHED';

  const content = document.createElement('div');
  content.innerHTML = `
    <dl class="space-y-3 text-sm">
      <div class="flex justify-between"><dt class="text-gray-500">From</dt><dd class="font-medium">${email.from_address}</dd></div>
      ${email.all_addresses?.length > 1 ? `<div><dt class="text-gray-500 mb-1">All Addresses</dt><dd class="text-xs text-gray-600">${email.all_addresses.join(', ')}</dd></div>` : ''}
      <div class="flex justify-between"><dt class="text-gray-500">Subject</dt><dd class="font-medium">${email.subject || '(none)'}</dd></div>
      <div class="flex justify-between"><dt class="text-gray-500">Received</dt><dd class="font-medium">${new Date(email.received_at).toLocaleString()}</dd></div>
      <div class="flex justify-between"><dt class="text-gray-500">Category</dt><dd><span class="badge badge-blue">${email.category}</span></dd></div>
      <div class="flex justify-between"><dt class="text-gray-500">Match</dt><dd><span class="badge badge-${email.match_method === 'unmatched' || !email.match_method ? 'yellow' : 'green'}">${email.match_method || 'unmatched'}</span></dd></div>
      ${property ? `<div class="flex justify-between"><dt class="text-gray-500">Property</dt><dd class="font-medium"><a href="#/properties/${property.id}" class="text-primary-600 hover:underline">${property.address}, ${property.suburb}</a></dd></div>` : ''}
    </dl>
    ${email.body_text ? `<div class="mt-4"><h4 class="text-sm font-semibold text-gray-700 mb-2">Email Body</h4><div class="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto border border-gray-200">${escapeHtml(email.body_text)}</div></div>` : ''}
    ${parsedInfo}
    ${attachmentsList}
    <div class="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t border-gray-200">
      <button class="btn-outline edit-email-btn text-sm">Edit</button>
      ${isMatched ? `
        <button class="btn-outline create-payment-btn text-sm">Create Payment</button>
        <button class="btn-outline create-maint-btn text-sm">Create Maintenance</button>
      ` : ''}
      <div class="flex-1"></div>
      <button class="btn-danger delete-email-detail-btn text-sm">Delete</button>
      <button class="btn-ghost close-detail-btn text-sm">Close</button>
    </div>
  `;

  const { close } = showModal('Email Details', content, { wide: true });
  content.querySelector('.close-detail-btn')!.addEventListener('click', close);

  // Delete button
  content.querySelector('.delete-email-detail-btn')!.addEventListener('click', async () => {
    const confirmed = await confirmModal('Delete this email? This cannot be undone.');
    if (!confirmed) return;
    try {
      await emails.delete(email.id);
      showToast('Email deleted', 'success');
      close();
      await reload();
    } catch {
      showToast('Failed to delete email', 'error');
    }
  });

  // Edit button
  content.querySelector('.edit-email-btn')!.addEventListener('click', () => {
    close();
    showEditEmailModal(email, propertyList, reload);
  });

  // Create Payment button
  content.querySelector('.create-payment-btn')?.addEventListener('click', async () => {
    const btn = content.querySelector('.create-payment-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Creating...';
    try {
      const res = await emails.createRecord(email.id, 'payment');
      showToast(res.created ? 'Payment record created' : 'Could not create — may already exist', res.created ? 'success' : 'info');
      close();
      await reload();
    } catch (err: any) {
      showToast(err.message || 'Failed to create payment', 'error');
      btn.disabled = false;
      btn.textContent = 'Create Payment';
    }
  });

  // Create Maintenance button
  content.querySelector('.create-maint-btn')?.addEventListener('click', async () => {
    const btn = content.querySelector('.create-maint-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Creating...';
    try {
      const res = await emails.createRecord(email.id, 'maintenance');
      showToast(res.created ? 'Maintenance record created' : 'Could not create — may already exist', res.created ? 'success' : 'info');
      close();
      await reload();
    } catch (err: any) {
      showToast(err.message || 'Failed to create maintenance', 'error');
      btn.disabled = false;
      btn.textContent = 'Create Maintenance';
    }
  });

  // Wire attachment download buttons
  content.addEventListener('click', async (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.email-attachment-link');
    if (!btn) return;
    const blobPath = btn.dataset.blobPath!;
    const pid = btn.dataset.propId!;
    const originalText = btn.innerHTML;
    btn.textContent = '⏳ Downloading…';
    try {
      const { url } = await documents.download(pid, blobPath);
      const a = document.createElement('a');
      a.href = url;
      a.download = blobPath.split('/').pop() || 'attachment';
      a.click();
    } catch {
      showToast('Failed to download attachment', 'error');
    } finally {
      btn.innerHTML = originalText;
    }
  });
}

function showEditEmailModal(email: InboundEmail, propertyList: Property[], reload: () => Promise<void>): void {
  const content = document.createElement('div');
  content.innerHTML = `
    <form id="edit-email-form" class="space-y-4">
      <div>
        <label class="label">Subject</label>
        <input name="subject" type="text" class="input-field" value="${escapeHtml(email.subject || '')}">
      </div>
      <div>
        <label class="label">Category</label>
        <select name="category" class="input-field">
          <option value="payment" ${email.category === 'payment' ? 'selected' : ''}>Payment</option>
          <option value="maintenance" ${email.category === 'maintenance' ? 'selected' : ''}>Maintenance</option>
          <option value="lease" ${email.category === 'lease' ? 'selected' : ''}>Lease</option>
          <option value="general" ${email.category === 'general' ? 'selected' : ''}>General</option>
        </select>
      </div>
      <div>
        <label class="label">Property</label>
        <select name="property_id" class="input-field">
          <option value="">Unmatched</option>
          ${propertyList.map(p => `<option value="${p.id}" ${email.property_id === p.id ? 'selected' : ''}>${p.address}, ${p.suburb} ${p.state} ${p.postcode}</option>`).join('')}
        </select>
      </div>
      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button type="button" class="btn-outline cancel-edit-btn">Cancel</button>
        <button type="submit" class="btn-primary">Save Changes</button>
      </div>
    </form>
  `;

  const { close } = showModal('Edit Email', content);
  content.querySelector('.cancel-edit-btn')!.addEventListener('click', close);

  content.querySelector('#edit-email-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data: Record<string, string> = {};
    const subject = (form.elements.namedItem('subject') as HTMLInputElement).value;
    const category = (form.elements.namedItem('category') as HTMLSelectElement).value;
    const propertyId = (form.elements.namedItem('property_id') as HTMLSelectElement).value;

    if (subject !== email.subject) data.subject = subject;
    if (category !== email.category) data.category = category;
    if (propertyId !== (email.property_id || '')) data.property_id = propertyId;

    if (Object.keys(data).length === 0) {
      close();
      return;
    }
    try {
      await emails.update(email.id, data);
      showToast('Email updated', 'success');
      close();
      await reload();
    } catch (err: any) {
      showToast(err.message || 'Failed to update email', 'error');
    }
  });
}

function showMatchModal(
  emailItem: InboundEmail,
  propertyList: Property[],
  reload: () => Promise<void>,
): void {
  const content = document.createElement('div');
  content.innerHTML = `
    <div class="mb-4">
      <p class="text-sm text-gray-500 mb-1">From: <strong>${emailItem.from_address}</strong></p>
      <p class="text-sm text-gray-500 mb-3">Subject: <strong>${emailItem.subject}</strong></p>
    </div>
    <div class="mb-4">
      <label class="label">Select Property</label>
      <select id="match-property-select" class="input-field">
        <option value="">— Choose a property —</option>
        ${propertyList.map(p => `<option value="${p.id}">${p.address}, ${p.suburb} ${p.state} ${p.postcode}</option>`).join('')}
      </select>
    </div>
    <div class="flex justify-end gap-3">
      <button class="btn-outline cancel-match-btn">Cancel</button>
      <button class="btn-primary confirm-match-btn" disabled>Match to Property</button>
    </div>
  `;

  const { close } = showModal('Match Email to Property', content);

  const select = content.querySelector('#match-property-select') as HTMLSelectElement;
  const confirmBtn = content.querySelector('.confirm-match-btn') as HTMLButtonElement;

  select.addEventListener('change', () => {
    confirmBtn.disabled = !select.value;
  });

  content.querySelector('.cancel-match-btn')!.addEventListener('click', close);

  confirmBtn.addEventListener('click', async () => {
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Matching...';
    try {
      await emails.match(emailItem.id, select.value);
      showToast('Email matched to property', 'success');
      close();
      await reload();
    } catch (err: any) {
      showToast(err.message || 'Failed to match', 'error');
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Match to Property';
    }
  });
}
